import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "../services/api";
import { Layers } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from "recharts";

export default function Dashboard() {
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchSummary() {
      try {
        const res = await axios.get("/authorized-reports/user");

        const grouped = Object.entries(res.data || {}).reduce((acc, [dept, reports]) => {
          acc[dept] = { count: reports.length, recent: null };
          reports.forEach((report) => {
            const rawDate = report.created_at || report.updated_at || report.timestamp;
            if (!rawDate) return;

            const parsedDate = new Date(rawDate);
            if (isNaN(parsedDate)) return;

            const isoDate = parsedDate.toISOString().split("T")[0];
            if (!acc[dept].recent || parsedDate > new Date(acc[dept].recent)) {
              acc[dept].recent = isoDate;
            }
          });
          return acc;
        }, {});

        setSummary(Object.entries(grouped));
      } catch (err) {
        console.error("Failed to fetch authorized report summary:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchSummary();
  }, []);

  const chartData = summary.map(([department, data]) => ({
    name: department,
    Reports: data.count
  }));

  return (
    <div className="p-6">
      <h2 className="text-4xl font-bold mb-8 text-center text-gray-800 flex items-center justify-center gap-2">
        <span role="img" aria-label="chart">📊</span> Report Summary
      </h2>

      {loading ? (
        <div className="text-center py-12 text-gray-400 animate-pulse text-lg">Loading dashboard...</div>
      ) : (
        <>
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3 mb-10">
            {summary.map(([department, data], i) => (
              <motion.div
                key={department}
                className="bg-gradient-to-br from-white to-blue-50 shadow-xl rounded-2xl p-6 border border-blue-100 hover:shadow-2xl transition-all"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xl font-semibold text-gray-800">{department}</div>
                  <Layers className="w-6 h-6 text-blue-500" />
                </div>
                <div className="text-sm text-gray-500">Total Reports</div>
                <div className="text-4xl font-bold text-blue-700">{data.count}</div>
                <div className="text-xs text-gray-400 mt-1">Last Updated: {data.recent || "N/A"}</div>
              </motion.div>
            ))}
          </div>

          <div className="p-6 bg-white rounded-2xl shadow border border-gray-100">
            <h3 className="text-xl font-semibold mb-4 text-gray-700 flex items-center gap-2">
              <span role="img" aria-label="bar">📈</span> Reports Per Department
            </h3>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #e5e7eb",
                      fontSize: "14px"
                    }}
                    labelStyle={{ fontWeight: "bold", color: "#4b5563" }}
                  />
                  <Bar dataKey="Reports" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center text-gray-400 py-8 italic">No report data to display.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
